import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Banknote, Ban, RotateCcw, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

const CAN_MANAGE = ["ORG_ADMIN", "ACCOUNTS"];
const EMPTY_ALLOCATION = { ra_bill_id: "", amount: "" };

export default function ClientPayments() {
  const { user } = useAuth();
  const canManage = CAN_MANAGE.includes(user?.role);
  const { companies } = useCompany() || {};
  const [payments, setPayments] = useState([]);
  const [clients, setClients] = useState([]);
  const [raBills, setRaBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [payRes, clientRes, billRes] = await Promise.all([
        api.get("/revenue/client-payments"), api.get("/revenue/clients"), api.get("/revenue/ra-bills"),
      ]);
      setPayments(payRes.data); setClients(clientRes.data); setRaBills(billRes.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

  const post = async (payment) => {
    setBusyId(payment.id);
    try {
      await api.post(`/revenue/client-payments/${payment.id}/post`);
      toast.success("Payment posted to GL & allocated to RA bills");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const fix = async (p, action) => {
    const text = {
      delete: `Delete draft ${p.number}?`,
      cancel: `Cancel ${p.number}? The bank entry is reversed and its RA bills become due again.`,
      revise: `Revise ${p.number}? It will be cancelled and a new DRAFT with the same details is created for you to correct and post.`,
    }[action];
    if (!window.confirm(text)) return;
    setBusyId(p.id);
    try {
      if (action === "delete") { await api.delete(`/revenue/client-payments/${p.id}`); toast.success("Draft deleted"); }
      else if (action === "cancel") { await api.post(`/revenue/client-payments/${p.id}/cancel`); toast.success(`${p.number} cancelled`); }
      else { const { data } = await api.post(`/revenue/client-payments/${p.id}/revise`); toast.success(`${data.cancelled} cancelled — correct draft ${data.new_draft_number} (✏️) and post it`); }
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="client-payments-page">
      <PageHeader title="Client Payments" description="Receive a client payment and allocate it against one or more outstanding RA bills." action={canManage && <CreatePaymentDialog companies={companies} clients={clients} raBills={raBills} payments={payments} onCreated={load} />} />
      <div className="rounded-xl border border-border bg-card">
        {!loading && payments.length === 0 ? (
          <EmptyState title="No client payments yet" testId="client-payments-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Number</TableHead><TableHead>Client</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {payments.map((p) => (
                <TableRow key={p.id} data-testid={`client-payment-row-${p.number}`}>
                  <TableCell className="font-mono text-xs text-foreground">{p.number}</TableCell>
                  <TableCell className="text-foreground">{clientName(p.client_id)}</TableCell>
                  <TableCell className="text-muted-foreground">{p.receipt_date}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold text-foreground">
                    {p.amount}
                    {(p.allocations || []).map((a) => <div key={a.id || a.ra_bill_id} className="font-sans text-[10px] font-normal text-muted-foreground">→ {raBills.find((b) => b.id === a.ra_bill_id)?.number || "RA bill"} · {a.amount}</div>)}
                  </TableCell>
                  <TableCell><StatusPill value={p.status} /></TableCell>
                  <TableCell className="text-right">
                    {canManage && p.status === "DRAFT" && (<>
                      <Button size="sm" variant="outline" disabled={busyId === p.id} onClick={() => post(p)} data-testid={`post-client-payment-button-${p.number}`}>
                        <Banknote className="h-3.5 w-3.5" /> Post
                      </Button>
                      <EditDraftPaymentDialog payment={p} raBills={raBills} onSaved={load} />
                      <Button size="sm" variant="ghost" title="Delete draft" disabled={busyId === p.id} onClick={() => fix(p, "delete")} data-testid={`delete-client-payment-${p.number}`}>
                        <Trash2 className="h-3.5 w-3.5 text-rose-400" />
                      </Button>
                    </>)}
                    {canManage && p.status === "POSTED" && (<>
                      <Button size="sm" variant="outline" title="Cancel this payment and open a corrected draft" disabled={busyId === p.id} onClick={() => fix(p, "revise")} data-testid={`revise-client-payment-${p.number}`}>
                        <RotateCcw className="h-3.5 w-3.5" /> Revise
                      </Button>
                      <Button size="sm" variant="ghost" title="Cancel (reverse) this payment" disabled={busyId === p.id} onClick={() => fix(p, "cancel")} data-testid={`cancel-client-payment-${p.number}`}>
                        <Ban className="h-3.5 w-3.5 text-rose-400" />
                      </Button>
                    </>)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreatePaymentDialog({ companies, clients, raBills, payments = [], onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", client_id: "", receipt_date: "", amount: "", remarks: "" });
  const [allocations, setAllocations] = useState([{ ...EMPTY_ALLOCATION }]);
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const updateAlloc = (idx, field, value) => {
    const next = allocations.map((a, i) => (i === idx ? { ...a, [field]: value } : a));
    setAllocations(next);
    if (field === "amount") {  // the payment amount always equals what is allocated to the bills
      const total = next.reduce((s2, a) => s2 + Number(a.amount || 0), 0);
      setForm((f) => ({ ...f, amount: total ? total.toFixed(2) : "" }));
    }
  };

  const money = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  // amount already allocated on DRAFT payments (not yet posted) counts as pending
  const draftAllocated = {};
  (payments || []).filter((p) => p.status === "DRAFT").forEach((p) => (p.allocations || []).forEach((a) => {
    draftAllocated[a.ra_bill_id] = (draftAllocated[a.ra_bill_id] || 0) + Number(a.amount || 0);
  }));
  const balanceOf = (b) => Math.max(Number(b.net_receivable || 0) - Number(b.received_amount || 0) - (draftAllocated[b.id] || 0), 0);
  // only posted bills of this client that still have something to receive, oldest first
  const clientBills = raBills
    .filter((b) => b.client_id === form.client_id && b.status === "POSTED" && balanceOf(b) > 0.005)
    .sort((a, b) => String(a.bill_date).localeCompare(String(b.bill_date)));
  const billById = Object.fromEntries(raBills.map((b) => [b.id, b]));
  const allocTotal = allocations.reduce((s2, a) => s2 + Number(a.amount || 0), 0);
  const overBill = allocations.some((a) => a.ra_bill_id && Number(a.amount || 0) > balanceOf(billById[a.ra_bill_id]) + 0.005);
  const overPayment = Number(form.amount || 0) > 0 && allocTotal > Number(form.amount) + 0.005;
  const unallocated = Number(form.amount || 0) - allocTotal;
  const notAllocated = Number(form.amount || 0) > 0 && unallocated > 0.005;
  const pickBill = (idx, id) => {
    const bill = billById[id];
    const others = allocations.reduce((s2, a, i) => (i === idx ? s2 : s2 + Number(a.amount || 0)), 0);
    const typed = Number(form.amount || 0);
    const fill = typed > others ? Math.min(balanceOf(bill), typed - others) : balanceOf(bill);
    const next = allocations.map((a, i) => (i === idx ? { ra_bill_id: id, amount: fill ? fill.toFixed(2) : "" } : a));
    setAllocations(next);
    const total = next.reduce((s2, a) => s2 + Number(a.amount || 0), 0);
    setForm((f) => ({ ...f, amount: total ? total.toFixed(2) : "" }));
  };
  // typing the payment amount spreads it over the chosen bills (oldest first, each up to its balance)
  const changeAmount = (value) => {
    setForm({ ...form, amount: value });
    const chosen = allocations.filter((a) => a.ra_bill_id);
    if (!chosen.length) return;
    let left = Number(value || 0);
    setAllocations(allocations.map((a) => {
      if (!a.ra_bill_id) return a;
      const take = Math.max(Math.min(balanceOf(billById[a.ra_bill_id]), left), 0);
      left -= take;
      return { ...a, amount: take ? take.toFixed(2) : "" };
    }));
  };
  const autoAllocate = () => {
    let left = Number(form.amount || 0);
    const rows = [];
    for (const b of clientBills) {
      if (left <= 0.005) break;
      const take = Math.min(balanceOf(b), left);
      rows.push({ ra_bill_id: b.id, amount: take.toFixed(2) });
      left -= take;
    }
    setAllocations(rows.length ? rows : [{ ...EMPTY_ALLOCATION }]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        allocations: allocations.filter((a) => a.ra_bill_id && a.amount).map((a) => ({ ra_bill_id: a.ra_bill_id, amount: a.amount })),
      };
      await api.post("/revenue/client-payments", payload);
      toast.success("Client payment created");
      setForm({ company_id: "", client_id: "", receipt_date: "", amount: "", remarks: "" });
      setAllocations([{ ...EMPTY_ALLOCATION }]);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-client-payment-button"><Plus className="h-4 w-4" /> New Payment</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Client Payment</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-client-payment-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Company</Label>
              <Select required value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="client-payment-company-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Client</Label>
              <Select required value={form.client_id} onValueChange={(v) => { setForm({ ...form, client_id: v }); setAllocations([{ ...EMPTY_ALLOCATION }]); }}>
                <SelectTrigger data-testid="client-payment-client-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(clients || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input required type="date" data-testid="client-payment-date-input" value={form.receipt_date} onChange={set("receipt_date")} className="mt-1.5" /></div>
            <div><Label>Amount</Label><Input required type="number" step="0.01" data-testid="client-payment-amount-input" value={form.amount} onChange={(e) => changeAmount(e.target.value)} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Against RA bills</Label>
            <div className="mt-1.5 space-y-2">
              {allocations.map((a, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`client-payment-allocation-row-${idx}`}>
                  <Select value={a.ra_bill_id} onValueChange={(v) => pickBill(idx, v)}>
                    <SelectTrigger className="flex-1" data-testid={`client-payment-allocation-bill-select-${idx}`}><SelectValue placeholder={form.client_id ? (clientBills.length ? "Select an RA bill" : "No unpaid bills for this client") : "Choose the client first"} /></SelectTrigger>
                    <SelectContent>
                      {clientBills
                        .filter((b) => b.id === a.ra_bill_id || !allocations.some((x, j) => j !== idx && x.ra_bill_id === b.id))
                        .map((b) => <SelectItem key={b.id} value={b.id}>{b.number} · {b.bill_date} · balance {money(balanceOf(b))} of {money(b.net_receivable)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input type="number" step="0.01" placeholder="Amount" className="w-28" value={a.amount} onChange={(e) => updateAlloc(idx, "amount", e.target.value)} data-testid={`client-payment-allocation-amount-input-${idx}`} />
                  {allocations.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => setAllocations(allocations.filter((_, i) => i !== idx))}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>}
                </div>
              ))}
              {allocations.map((a, idx) => a.ra_bill_id && Number(a.amount || 0) > balanceOf(billById[a.ra_bill_id]) + 0.005 && (
                <p key={`w${idx}`} className="text-xs text-rose-600 dark:text-rose-400">{billById[a.ra_bill_id]?.number}: more than its balance {money(balanceOf(billById[a.ra_bill_id]))}</p>
              ))}
              {overPayment && <p className="text-xs text-rose-600 dark:text-rose-400">Allocated {money(allocTotal)} is more than the payment {money(form.amount)}</p>}
              {notAllocated && (
                <p className="text-xs text-rose-600 dark:text-rose-400">
                  {money(unallocated)} is more than the balance of the chosen bills. Receive only what is due{clientBills.length ? " (add another bill)" : ""}, or record the extra as a <b>Client Advance</b>.
                </p>
              )}
              {form.client_id && clientBills.length > 0 && (
                <p className="text-xs text-muted-foreground">Unpaid bills of this client: {money(clientBills.reduce((s2, b) => s2 + balanceOf(b), 0))}</p>
              )}
            </div>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setAllocations([...allocations, { ...EMPTY_ALLOCATION }])} data-testid="add-client-payment-allocation-button"><Plus className="h-3.5 w-3.5" /> Add allocation</Button>
            {Number(form.amount) > 0 && clientBills.length > 0 && (
              <Button type="button" size="sm" variant="outline" className="ml-2 mt-2" onClick={autoAllocate} data-testid="client-payment-auto-allocate">Allocate oldest first</Button>
            )}
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving || overBill || overPayment || notAllocated || allocTotal <= 0} data-testid="create-client-payment-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditDraftPaymentDialog({ payment, raBills, onSaved }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(payment.receipt_date);
  const [remarks, setRemarks] = useState(payment.remarks || "");
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setDate(payment.receipt_date);
      setRemarks(payment.remarks || "");
      setLines((payment.allocations || []).map((a) => ({ ra_bill_id: a.ra_bill_id, amount: String(a.amount) })));
    }
  }, [open, payment]);
  const bill = (id) => raBills.find((b) => b.id === id);
  const balance = (b) => (b ? Math.max(Number(b.net_receivable || 0) - Number(b.received_amount || 0), 0) : 0);
  const total = lines.reduce((s2, l) => s2 + Number(l.amount || 0), 0);
  const over = lines.some((l) => Number(l.amount || 0) > balance(bill(l.ra_bill_id)) + 0.005);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/revenue/client-payments/${payment.id}`, { receipt_date: date, remarks: remarks || null, allocations: lines.filter((l) => Number(l.amount) > 0) });
      toast.success("Draft updated");
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="ghost" title="Edit draft" data-testid={`edit-client-payment-${payment.number}`}><Pencil className="h-3.5 w-3.5" /></Button></DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Edit {payment.number}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div><Label>Date</Label><Input required type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" /></div>
          <div className="space-y-2">
            <Label>Amount per RA bill</Label>
            {lines.length === 0 && <p className="text-xs text-muted-foreground">No bills on this draft — delete it and create a new payment.</p>}
            {lines.map((l, idx) => (
              <div key={l.ra_bill_id} className="flex items-center gap-2">
                <span className="flex-1 text-sm">{bill(l.ra_bill_id)?.number || "RA bill"} <span className="text-xs text-muted-foreground">(balance ₹{balance(bill(l.ra_bill_id)).toLocaleString("en-IN")})</span></span>
                <Input type="number" step="0.01" min="0" className="w-32" value={l.amount} onChange={(e) => setLines(lines.map((x, i) => (i === idx ? { ...x, amount: e.target.value } : x)))} />
                <Button type="button" size="sm" variant="ghost" onClick={() => setLines(lines.filter((_, i) => i !== idx))}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>
              </div>
            ))}
            {over && <p className="text-xs text-rose-600">A line is more than that bill's balance</p>}
            <p className="text-sm">Payment amount: <b className="font-mono">₹{total.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</b></p>
          </div>
          <div><Label>Remarks</Label><Input value={remarks} onChange={(e) => setRemarks(e.target.value)} className="mt-1.5" /></div>
          <DialogFooter><Button type="submit" disabled={saving || over || total <= 0}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
